import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { tracker } from '../../../tracker';
import { PlaybackEndedEvent } from '../../../tracker/events/playback-ended';
import { PlaybackStartedEvent } from '../../../tracker/events/playback-started';
import { bookmarksKey } from '../../../queries/ats/me/bookmarks/keys';
import { bookmarksQuery } from '../../../queries/ats/me/bookmarks/queries';
import { Bookmark } from '../../../queries/ats/me/bookmarks/types';
import { continueWatchingKey } from '../../../queries/ats/me/keys';
import { playRecording } from '../../../queries/ats/playback/adapters/play-recording';
import { PlayData, reportConsumption as report, stopConsumption as stop } from '../../../queries/ats/playback/api';
import { SingleRecording } from '../../../queries/ats/recordings/types/recording';
import { createSingleRecordingTitle } from '../../../queries/ats/recordings/utils/title-adapter';
import { PlayablePlaybackOption } from '../../../queries/ats/titles/types/playback-option';
import { PlayableTitle } from '../../../queries/ats/titles/types/title';
import { queryClient } from '../../../queries/client';
import { genresQuery } from '../../../queries/genres/queries';

async function getBookmark(platformTitleId: string) {
  try {
    const bookmarks = await queryClient.fetchQuery(bookmarksQuery());
    return bookmarks.find((bookmark) => bookmark.platformTitleId === platformTitleId);
  } catch (error) {
    return undefined;
  }
}

export type PlayPvrResponse = [PlayData, PlayableTitle, PlayablePlaybackOption, Bookmark | undefined];

async function playPvr(recording: SingleRecording, shouldStartSession = true) {
  const bookmark = await getBookmark(recording.programId);
  const data = await playRecording(recording, shouldStartSession);
  const genres = await queryClient.fetchQuery(genresQuery());
  const playableTitle = createSingleRecordingTitle(recording, genres);
  // On a fake title created from a recording, we only have one PO.
  const playbackOption = playableTitle.playbackOptions[0];
  tracker.track(new PlaybackStartedEvent(playableTitle, playbackOption, bookmark?.position ?? 0, false));
  return [data, playableTitle, playbackOption, bookmark] as const;
}

interface PlayPvrOptions {
  recording: SingleRecording;
  startSession?: boolean;
}

export function usePlayRecording(options: PlayPvrOptions) {
  const [data, setData] = useState<PlayData>();
  const [title, setTitle] = useState<PlayableTitle>();
  const [playbackOption, setPlaybackOption] = useState<PlayablePlaybackOption>();
  const [bookmark, setBookmark] = useState<Bookmark | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const reportConsumption = useCallback(
    async (position: number) => {
      if (data === undefined) {
        console.error('Cannot report consumption without session id.');
        return;
      }

      // We have to ignore any position under the start offset, and any position
      // over the end offset.
      const startOffset = moment.duration(options.recording.startOffset, 'm');
      const endOffset = moment.duration(options.recording.endOffset, 'm');

      if (position <= startOffset.asSeconds()) {
        // If we don't report consumption, the session will terminate, so we
        // report consumption at position 0 during the start offset.
        await report({ sessionId: data.sessionId, position: 0 });
        return;
      }

      const clampedPosition = Math.min(position, options.recording.duration + endOffset.asSeconds());

      // We don't want to invalidate bookmarks here, as we subscribe to bookmarks
      // in the player, which would casue the player to re-fetch bookmarks.
      await report({ sessionId: data.sessionId, position: clampedPosition });
    },
    [data, options.recording.duration, options.recording.endOffset, options.recording.startOffset],
  );

  const stopConsumption = useCallback(
    async (position: number) => {
      if (data === undefined) {
        console.error('Cannot stop consumption without session id.');
        return;
      }

      if (title !== undefined && playbackOption !== undefined) {
        tracker.track(new PlaybackEndedEvent(title, playbackOption, position, false));
      }

      await stop({ sessionId: data.sessionId, position });
      await queryClient.invalidateQueries(bookmarksKey());
      await queryClient.invalidateQueries(continueWatchingKey());
    },
    [data, playbackOption, title],
  );

  const fetchPlay = useCallback(async (recording: SingleRecording, shouldStartSession: boolean) => {
    try {
      const response = await playPvr(recording, shouldStartSession);

      // Since we are still on React 17, state updates are not
      // automatically batched in async functions. We need to wrap
      // the state updates in a batchedUpdates call.
      batchedUpdates(() => {
        setData(response[0]);
        setTitle(response[1]);
        setPlaybackOption(response[2]);
        setBookmark(response[3]);
      });
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlay(options.recording, options.startSession ?? true);
  }, [fetchPlay, options.recording, options.startSession]);

  if (error) {
    throw error;
  }

  return { data, bookmark, isLoading, reportConsumption, stopConsumption };
}
